// Die Karte „Bestellung“ in der Akte (E3c, S3 – PLAN.md, „E3c“, S3;
// ANFORDERUNGEN.md H27 bis H30, H34, G17, G19, G20), am Laptop und am iPad:
//
//  1. Die Module: Anzahl und Fabrikat – das Fabrikat frei, die bisher
//     verwendeten zum Antippen (G17). Draußen in der HE-Doku nicht gefragt.
//  2. Je Teil der Lieferant zum Antippen und der Wunschliefertermin (Vorschlag
//     aus dem Montagetermin, änderbar); Wechselrichter und Speicher nur, wenn
//     sie dazukommen (H30). Was bestellt ist, steht mit wann und wer da –
//     „ändern“ öffnet es wieder (Befund 7).
//  3. „Bestellen per Mail“ – VOR dem Klick (G20): je Lieferant ein Satz
//     („Unterkonstruktion + Module → Gautzsch, Lieferung Fr., 4. Dez.“),
//     darunter „Noch von Hand“ und drei Schritte (seit Gernots Augentest,
//     3.11.2026): die Dateien herunterladen (K2-Liste als Excel, K2-Plan als
//     PDF), die Mail öffnen (mailto:, Weg A – Vorschau, an wen), „Bestellt“ –
//     erst nach dem Abschicken.
//
// Was noch nicht geht, sagt warum (G19). Der Text der Mail und der Link
// bleiben im Browser (ablauf.ts).

import { useState, type FormEvent } from "react";
import {
  ABLAUF_GRENZEN,
  ANLASS_WORTE,
  BESTELL_TEIL_NAMEN,
  BESTELL_TEILE,
  lieferterminFuer,
  moduleEingabe,
  schrittIn,
  vorlaufText,
  type AblaufBestellung,
  type AblaufEinstellungen,
  type Akte,
  type BestellTeil,
  type PartnerZeile,
} from "@happy-energy/kern";
import { Chip, Ikon } from "@happy-energy/oberflaeche";
import { dateiAdresse, kalendertagZeigen, tagZeigen } from "../anzeige.ts";
import { dachplanungDateiname } from "../freigabe/dachplanung.ts";
import { dateiRahmen } from "../freigabe/DachplanungKarte.tsx";
import { useCrm } from "../zusammenhang.tsx";
import { bestellGruppen, bestellMail, gruppenSatz, k2ExcelDateiname, mailtoLink, nurAnsprechpartner, tagKurz, type BestellGruppe } from "./ablauf.ts";
import { AnWen, K2ExcelKnopf, MailSchritt, Meldung, NochVonHand, PartnerWahl, SchonErledigtKnopf, TagFeld, TextKopieren, useSchicken } from "./gemeinsam.tsx";
import { KartenKopf, StandSatz } from "./kopf.tsx";

/** Die jüngste geltende Bestellung eines Teils – an ihr ändert die Karte. */
function bestellungVon(akte: Akte, teil: BestellTeil): AblaufBestellung | null {
  const zeilen = akte.ablauf.bestellungen.filter((b) => b.teil === teil);
  return zeilen.at(-1) ?? null;
}

/** Der Rahmen der Dateinamen: der Tag der ersten Besichtigung, die Produktlinie (wie in der Akte, O76). */
export function rahmenDerAkte(akte: Akte) {
  return dateiRahmen(akte.aufnahmen.map((x) => x.begonnenAm).sort()[0], akte.anlage.produktlinie);
}

/** Liegt bei der Anlage eine Datei aus K2 – die Liste (Excel) oder der Plan (PDF)? */
export function hatK2Dateien(akte: Akte): boolean {
  return Boolean(akte.ablauf.k2Excel) || Boolean(akte.anlage.k2.pruefsumme);
}

/**
 * In Schritt 1 jeder Mail: beide Dateien aus K2 zum Herunterladen, sobald sie
 * bei der Anlage liegen – bei jedem Lieferanten und bei der Dachpartie; was
 * wer braucht, entscheidet, wer die Mail schickt (Gernot, 3.11.2026 abends:
 * „bei jedem Lieferanten, was er möchte“). `fehltSagen`: Wo die Dateien
 * gebraucht werden (Unterkonstruktion, Module), steht auch, was noch fehlt –
 * die Liste mit „Excel hochladen“; sonst nur, was da ist.
 */
export function K2Dateien({ akte, fehltSagen, neuLaden }: { akte: Akte; fehltSagen: boolean; neuLaden: () => void }) {
  const rahmen = rahmenDerAkte(akte);
  return (
    <>
      {akte.ablauf.k2Excel ? (
        <a className="crm-knopf rand klein" href={dateiAdresse(akte.ablauf.k2Excel)} download={k2ExcelDateiname(akte.kunde.name, rahmen)}>
          <Ikon name="herunterladen" groesse="k" /> K2-Liste (Excel) herunterladen
        </a>
      ) : (
        fehltSagen && (
          <span className="ablauf-fehlt">
            <span className="akte-geaendert klein">Die K2-Liste (Excel) fehlt noch – zuerst vom Computer ins CRM hochladen:</span>
            <K2ExcelKnopf anlageId={akte.anlage.id} da={false} fertig={neuLaden} />
          </span>
        )
      )}
      {akte.anlage.k2.pruefsumme ? (
        <a className="crm-knopf rand klein" href={dateiAdresse(akte.anlage.k2.pruefsumme)} download={dachplanungDateiname(akte.kunde.name, rahmen)}>
          <Ikon name="herunterladen" groesse="k" /> K2-Plan (PDF) herunterladen
        </a>
      ) : (
        fehltSagen && <span className="akte-geaendert klein">Der K2-Plan (PDF) fehlt noch – er kommt mit der Dachplanung.</span>
      )}
    </>
  );
}

// --- 1. Die Module ----------------------------------------------------------------------------

function ModuleFelder({ akte, schicken, sendet }: { akte: Akte; schicken: ReturnType<typeof useSchicken>["schicken"]; sendet: boolean }) {
  const m = akte.ablauf.module;
  const [anzahl, setAnzahl] = useState(m.anzahl === null ? "" : String(m.anzahl));
  const [fabrikat, setFabrikat] = useState(m.fabrikat ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const [stand, setStand] = useState(m);
  // Kommt vom Server ein anderer Stand (neu geladen), gilt er.
  if (stand.anzahl !== m.anzahl || stand.fabrikat !== m.fabrikat) {
    setStand(m);
    setAnzahl(m.anzahl === null ? "" : String(m.anzahl));
    setFabrikat(m.fabrikat ?? "");
  }
  const geaendert = anzahl.trim() !== (m.anzahl === null ? "" : String(m.anzahl)) || fabrikat.trim() !== (m.fabrikat ?? "");
  const bestellt = bestellungVon(akte, "module");
  const schonBestellt = bestellt !== null && bestellt.erledigtAm !== null && bestellt.weg === "mail";

  function speichern(e: FormEvent) {
    e.preventDefault();
    const gelesen = moduleEingabe.safeParse({ anzahl: anzahl.trim() === "" ? null : Number(anzahl), fabrikat: fabrikat.trim() || null });
    if (!gelesen.success) {
      setFehler(gelesen.error.issues[0]?.message ?? "Bitte prüfen.");
      return;
    }
    setFehler(null);
    void schicken("module", gelesen.data, "Module gespeichert");
  }

  const vorschlaege = akte.ablauf.fabrikate.filter((f) => f !== fabrikat.trim());
  return (
    <form className="ablauf-module" aria-label="Module" onSubmit={speichern}>
      <div className="ablauf-felder">
        <label className="crm-feld ablauf-anzahl">
          Anzahl Module
          <input className="crm-eingabe" inputMode="numeric" value={anzahl} onChange={(e) => setAnzahl(e.target.value.replace(/[^\d]/g, ""))} maxLength={5} />
        </label>
        <label className="crm-feld ablauf-fabrikat">
          Fabrikat
          <input className="crm-eingabe" value={fabrikat} maxLength={ABLAUF_GRENZEN.fabrikat} onChange={(e) => setFabrikat(e.target.value)} placeholder="z. B. Trina Vertex S+" />
        </label>
        <button type="submit" className="crm-knopf lime klein" disabled={!geaendert || sendet}>
          <Ikon name="haken" groesse="k" /> Speichern
        </button>
      </div>
      {vorschlaege.length > 0 && (
        <div className="ablauf-vorschlaege" aria-label="Bisher verwendete Fabrikate">
          <span className="leise klein">Bisher verwendet:</span>
          {vorschlaege.map((f) => (
            <button key={f} type="button" className="crm-knopf rand klein" onClick={() => setFabrikat(f)}>
              {f}
            </button>
          ))}
        </div>
      )}
      {/* Gernots Augentest, 3.11.: Er tippte Anzahl und Fabrikat, sah „Speichern“ ganz rechts nicht, und die Mail blieb gesperrt. */}
      {geaendert && !sendet && (
        <p className="akte-geaendert klein">
          {schonBestellt ? "Die Module sind schon bestellt – speichern öffnet die Bestellung wieder (Mail erneut)." : "Noch nicht gespeichert – bitte „Speichern“."}
        </p>
      )}
      <Meldung text={fehler} />
    </form>
  );
}

// --- 2. Je Teil -------------------------------------------------------------------------------

function TeilZeile({
  akte,
  teil,
  lieferanten,
  vorschlag,
  werktage,
  freigegeben,
  schicken,
  sendet,
}: {
  akte: Akte;
  teil: BestellTeil;
  lieferanten: readonly PartnerZeile[];
  vorschlag: string | null;
  /** Der Vorlauf der Firma – neben dem vorgeschlagenen Tag steht, wie er gerechnet ist. */
  werktage: number;
  freigegeben: boolean;
  schicken: ReturnType<typeof useSchicken>["schicken"];
  sendet: boolean;
}) {
  const b = bestellungVon(akte, teil);
  const [aendern, setAendern] = useState(false);
  const [dazu, setDazu] = useState(false);
  const erledigt = b !== null && b.erledigtAm !== null;
  const optional = teil === "wechselrichter_speicher";
  const name = BESTELL_TEIL_NAMEN[teil];
  const lieferant = b?.lieferantId ? lieferanten.find((p) => p.id === b.lieferantId) : undefined;

  const setzen = (lieferantId: string | null, wunschliefertermin: string | null) =>
    schicken("bestellung", { teil, lieferantId, wunschliefertermin }, lieferantId === null ? `${name}: kommt nicht dazu` : undefined).then((ok) => {
      if (ok) setAendern(false);
    });

  // Erledigt: wann, bei wem, wer – „ändern“ öffnet die Felder (eine Änderung öffnet die Bestellung wieder).
  if (erledigt && !aendern) {
    return (
      <li className="ablauf-teil erledigt" aria-label={name}>
        <div className="ablauf-teil-kopf">
          <b>{name}</b>
          <Chip art="ok" ikon="haken">
            {b.weg === "schon-erledigt" ? "schon erledigt" : "bestellt"}
          </Chip>
        </div>
        <p className="klein">
          {b.weg === "schon-erledigt"
            ? `als schon erledigt vermerkt (ohne System)${lieferant ? ` – bei ${lieferant.name}` : ""}`
            : `bei ${lieferant?.name ?? "einem Lieferanten"}${b.wunschliefertermin ? ` · Lieferung ${kalendertagZeigen(b.wunschliefertermin)}` : ""} – bestellt ${tagZeigen(b.erledigtAm!)}${b.erledigtVon ? ` (${b.erledigtVon.name})` : ""}`}
        </p>
        {b.weg === "mail" && (
          <button type="button" className="crm-knopf leise klein" onClick={() => setAendern(true)}>
            ändern
          </button>
        )}
      </li>
    );
  }

  // Wechselrichter und Speicher: nur, wenn sie dazukommen (H30).
  if (optional && b === null && !dazu) {
    return (
      <li className="ablauf-teil" aria-label={name}>
        <div className="ablauf-teil-kopf">
          <b>{name}</b>
          <label className="ablauf-schalter">
            <input type="checkbox" checked={false} onChange={() => setDazu(true)} /> kommt dazu
          </label>
        </div>
        <p className="leise klein">Nur ankreuzen, wenn sie mitbestellt werden.</p>
      </li>
    );
  }

  const wunsch = b?.wunschliefertermin ?? vorschlag;
  return (
    <li className={`ablauf-teil${b?.vorgaengerErledigt ? " wieder" : ""}`} aria-label={name}>
      <div className="ablauf-teil-kopf">
        <b>{name}</b>
        {optional && (
          <label className="ablauf-schalter">
            <input
              type="checkbox"
              checked
              disabled={sendet}
              onChange={() => {
                if (b) void setzen(null, null);
                else setDazu(false);
              }}
            />{" "}
            kommt dazu
          </label>
        )}
        {b && b.erledigtAm === null && b.vorgaengerErledigt && (
          <Chip art="warn">{b.anlass && b.anlass !== "vor-dem-update" ? `${ANLASS_WORTE[b.anlass]} – Mail erneut öffnen` : "wieder offen"}</Chip>
        )}
      </div>
      {aendern && <p className="akte-geaendert klein">Schon bestellt – eine Änderung öffnet die Bestellung wieder (Mail erneut öffnen).</p>}
      <PartnerWahl name={`Lieferant für ${name}`} partner={lieferanten} gewaehlt={b?.lieferantId ?? null} gesperrt={sendet} waehlen={(id) => void setzen(id, b?.wunschliefertermin ?? null)} />
      <div className="ablauf-felder">
        <TagFeld
          name="Wunschliefertermin"
          wert={wunsch ?? ""}
          gesperrt={sendet || !b?.lieferantId}
          {...(b?.lieferantId ? {} : { titel: "Zuerst den Lieferanten antippen" })}
          speichern={(tag) => {
            if (b?.lieferantId) void setzen(b.lieferantId, tag);
          }}
        />
        <span className="leise klein ablauf-tag" title="Vorschlag aus dem Montagetermin – den Vorlauf stellst du unter Einstellungen → „Lieferanten und Partien“ ein.">
          {wunsch ? `${tagKurz(wunsch)}${wunsch === vorschlag ? ` – ${vorlaufText(werktage)}` : ""}` : "kommt aus dem Montagetermin"}
        </span>
        {aendern && (
          <button type="button" className="crm-knopf rand klein" onClick={() => setAendern(false)}>
            Fertig
          </button>
        )}
      </div>
      {!erledigt && freigegeben && (
        <SchonErledigtKnopf frage={`${name} schon ohne System bestellt?`} sendet={sendet} los={() => void schicken("bestellung/schon-erledigt", { teil }, `${name}: als schon erledigt vermerkt`)} />
      )}
    </li>
  );
}

// --- 3. Bestellen per Mail ---------------------------------------------------------------------------

function MailAnLieferant({
  akte,
  g,
  einstellungen,
  gehtNicht,
  schicken,
  sendet,
  neuLaden,
  erneut = false,
}: {
  akte: Akte;
  g: BestellGruppe;
  einstellungen: AblaufEinstellungen;
  gehtNicht: string | null;
  schicken: ReturnType<typeof useSchicken>["schicken"];
  sendet: boolean;
  neuLaden: () => void;
  /** Seit S7: Die K2-Liste kam nach „Bestellt“ – dieselbe Mail noch einmal, die Bestellung bleibt abgehakt. */
  erneut?: boolean;
}) {
  const { ich } = useCrm();
  const [wer, setWer] = useState(0);
  const mail = bestellMail({ ...g, lieferant: nurAnsprechpartner(g.lieferant, wer) }, { vorlage: einstellungen.mailvorlagen.bestellung, objekt: akte.objekt, firma: ich.firma.name, module: akte.ablauf.module });
  const link = mailtoLink(mail.an, mail.betreff, mail.text);
  const blockiert = gehtNicht ?? (mail.fehlt.length > 0 ? `Es fehlt noch: ${mail.fehlt.join(", ")}.` : null);
  const mitUnterkonstruktion = g.teile.some((t) => t.teil === "unterkonstruktion" || t.teil === "module");
  // Die Dateien aus K2 stehen bei jedem Lieferanten, sobald es welche gibt; bei Unterkonstruktion und Modulen auch, was fehlt.
  const mitDateien = mitUnterkonstruktion || hatK2Dateien(akte);
  const name = g.lieferant.name;

  // Gernots Augentest E3c (3.11.2026): Hochladen und Herunterladen standen in einer Zeile „zum Anhängen“, die Regel zum Liefertermin
  // darüber – zu viel auf einmal. Jetzt der Reihe nach: erst die Dateien holen, dann die Mail, dann abhaken; darüber, was noch von Hand geht.
  // Ohne Dateien beginnt es bei der Mail.
  const vorDerMail = mitDateien ? 1 : 0;
  return (
    <section className="ablauf-mail" aria-label={`Bestellung bei ${name}`}>
      <p className="ablauf-satz-gross">
        <b>{gruppenSatz(g)}</b>
      </p>
      {mitDateien && <NochVonHand />}
      <ol className="ablauf-schritte">
        {mitDateien && (
          <MailSchritt nr={1} titel="Die Dateien herunterladen">
            <div className="ablauf-dateien">
              <K2Dateien akte={akte} fehltSagen={mitUnterkonstruktion} neuLaden={neuLaden} />
            </div>
          </MailSchritt>
        )}
        <MailSchritt nr={vorDerMail + 1} titel={mitDateien ? "Die Mail öffnen, die Dateien anhängen, abschicken" : "Die Mail öffnen und abschicken"}>
          <AnWen p={g.lieferant} an={mail.an} nr={wer} waehlen={setWer} />
          <div className="ablauf-vorschau" role="group" aria-label={`Mail an ${name} – Vorschau`}>
            <p className="klein">
              <span className="leise">Betreff:</span> {mail.betreff}
            </p>
            <pre>{mail.text}</pre>
          </div>
          {blockiert && <p className="akte-geaendert klein">{blockiert}</p>}
          {mail.an.length > 0 && !link.mitText && !blockiert && (
            <p className="leise klein">Der Text ist zu lang für den Link – die Mail öffnet sich ohne Text; bitte „Text kopieren“ und einfügen.</p>
          )}
          <div className="crm-knopf-reihe">
            {/* Während gespeichert wird, zeigt die Karte noch den alten Stand – dann kein Link. */}
            {mail.an.length > 0 && !blockiert && !sendet ? (
              <a className="crm-knopf dunkel" href={link.href}>
                <Ikon name="senden" groesse="k" /> Mail an {name} öffnen
              </a>
            ) : (
              <button type="button" className="crm-knopf dunkel" disabled title={blockiert ?? "Keine Mail-Adresse – bitte den Text kopieren"}>
                <Ikon name="senden" groesse="k" /> Mail an {name} öffnen
              </button>
            )}
            {(mail.an.length === 0 || !link.mitText) && <TextKopieren text={mail.text} wo="Bestellung" />}
          </div>
        </MailSchritt>
        {erneut ? (
          <MailSchritt nr={vorDerMail + 2} titel="Nichts abzuhaken">
            <p className="leise klein">Die Bestellung bleibt abgehakt – die Mail mit der neuen K2-Liste nur noch einmal schicken.</p>
          </MailSchritt>
        ) : (
          <MailSchritt nr={vorDerMail + 2} titel="Abhaken">
            <div className="crm-knopf-reihe">
              <button
                type="button"
                className="crm-knopf lime"
                disabled={sendet || blockiert !== null}
                onClick={() =>
                  void schicken(
                    "bestellt",
                    { lieferantId: g.lieferant.id, bestellungen: g.teile.map((t) => t.id) },
                    `Bei ${name} bestellt: ${g.teile.map((t) => BESTELL_TEIL_NAMEN[t.teil]).join(", ")}`,
                  )
                }
              >
                <Ikon name="haken" groesse="k" /> Bestellt
              </button>
              <span className="leise klein">Erst, wenn die Mail an {name} abgeschickt ist.</span>
            </div>
          </MailSchritt>
        )}
      </ol>
    </section>
  );
}

/** Seit S7: die Gruppe für „erneut senden“ – die Unterkonstruktion (mit ihr geht die K2-Liste) bei ihrem Lieferanten, per Mail bestellt. */
function erneutGruppe(akte: Akte, partner: readonly PartnerZeile[]): BestellGruppe | null {
  const uk = bestellungVon(akte, "unterkonstruktion");
  if (!uk || uk.erledigtAm === null || uk.weg !== "mail" || !uk.lieferantId) return null;
  const lieferant = partner.find((p) => p.id === uk.lieferantId);
  if (!lieferant) return null;
  const teile = akte.ablauf.bestellungen.filter((b) => b.lieferantId === uk.lieferantId && b.erledigtAm !== null && b.weg === "mail");
  return { lieferant, teile };
}

// --- Die Karte ----------------------------------------------------------------------------------------

export function BestellungKarte({ akte, partner, einstellungen, neuLaden }: { akte: Akte; partner: readonly PartnerZeile[]; einstellungen: AblaufEinstellungen; neuLaden: () => void }) {
  const { sendet, meldung, schicken } = useSchicken(akte.anlage.id, "Bestellung", neuLaden);
  const s = schrittIn(akte.kette, "bestellt");
  const freigegeben = ["erledigt", "vor-dem-update"].includes(schrittIn(akte.kette, "freigeben").stand);
  const lieferanten = partner.filter((p) => p.art === "lieferant");
  const vorschlag = lieferterminFuer(akte.anlage.montagetermin.am, einstellungen.lieferVorlaufWerktage);
  const gruppen = bestellGruppen(akte.ablauf.bestellungen, partner);
  const gehtNicht = s.stand === "geht-noch-nicht" ? `Bestellen ${s.grund ?? "geht noch nicht"}.` : null;
  const erneut = s.hinweis !== null ? erneutGruppe(akte, partner) : null;

  return (
    <>
      <KartenKopf ikon="paket" titel="Bestellung" schritt={s} />
      <StandSatz schritt={s} />

      <ModuleFelder akte={akte} schicken={schicken} sendet={sendet} />

      <ul className="ablauf-teile" aria-label="Teile">
        {BESTELL_TEILE.map((teil) => (
          <TeilZeile
            key={teil}
            akte={akte}
            teil={teil}
            lieferanten={lieferanten}
            vorschlag={vorschlag}
            werktage={einstellungen.lieferVorlaufWerktage}
            freigegeben={freigegeben}
            schicken={schicken}
            sendet={sendet}
          />
        ))}
      </ul>
      <Meldung text={meldung} />

      <h4 className="ablauf-zwischen">Bestellen per Mail</h4>
      {gruppen.length === 0 ? (
        <p className="leise klein">
          {akte.ablauf.bestellungen.some((b) => b.erledigtAm === null)
            ? "Der Lieferant ist nicht mehr in der Liste – bitte die Seite neu laden."
            : akte.ablauf.bestellungen.length > 0 && akte.ablauf.bestellungen.every((b) => b.erledigtAm !== null)
              ? "Alles bestellt."
              : "Sobald je Teil ein Lieferant angetippt ist, steht hier die Mail – je Lieferant eine."}
        </p>
      ) : (
        gruppen.map((g) => <MailAnLieferant key={g.lieferant.id} akte={akte} g={g} einstellungen={einstellungen} gehtNicht={gehtNicht} schicken={schicken} sendet={sendet} neuLaden={neuLaden} />)
      )}
      {erneut && <MailAnLieferant akte={akte} g={erneut} einstellungen={einstellungen} gehtNicht={null} schicken={schicken} sendet={sendet} neuLaden={neuLaden} erneut />}
    </>
  );
}
